// Contribution from Mercatura DeFi Team to Menese Protocol
// (2-of-2 Threshold ECDSA — Tier 7 §3.C WebAuthn-PRF custody).
// Mercatura Forum proprietary.
//
// WebAuthn Level 3 PRF extension wraps the user's `x_user`
// (secp256k1 ECDSA share) under a passkey-derived KEK. Cross-device
// custody works because the same passkey synced via the user's OS
// keychain (Apple Keychain, Google Password Manager, 1Password,
// Yubikey) produces the same PRF output everywhere it's available.
//
// Threat model:
//   - The canister + node providers NEVER see plaintext `x_user` —
//     only the AEAD-wrapped blob, which is opaque without the
//     passkey.
//   - A passkey thief who DOES NOT have the user's biometric / PIN
//     cannot trigger PRF derivation (passkeys are gated by user
//     verification on the device).
//   - Loss of the passkey = loss of `x_user` access. Recovery is
//     FH-7 (out of scope for T7.5).
//
// Anti-stub posture (Tier-7 §1.A, §1.D):
//   - x_user NEVER touches localStorage / sessionStorage / IndexedDB.
//   - The PRF salt NEVER persists; re-derived on every unwrap call.
//   - The credential ID (a public handle) DOES persist in
//     localStorage so the user can `get()` the same passkey again.

import type { Principal } from "@dfinity/principal";

/** Locked PRF input — same string across all menese-2of2 deployments
 * so the SAME passkey on a different device produces the SAME PRF
 * output, enabling cross-device unwrap. Mixing in the principal
 * would BREAK cross-device because the principal IS the same across
 * devices for II logins, so this is fine — but we keep it constant
 * to leave the per-app salt out of the device-attestable input. */
const PRF_INPUT_SALT = new TextEncoder().encode(
  "menese-2of2-x_user-wrap-v1",
);

/** AEAD info string for HKDF — domain-separates the AES-GCM key
 * from any other key the same PRF output might be used to derive. */
const HKDF_INFO_AEAD = new TextEncoder().encode(
  "menese-2of2-aead-aes-gcm-256-v1",
);

const RP_ID = (() => {
  if (typeof window === "undefined") return "localhost";
  return window.location.hostname;
})();

const RP_NAME = "Menese 2-of-2";

/** localStorage key under which the registered credential ID lives.
 * The credential ID is a PUBLIC handle (PUBLIC, not secret) — losing
 * it is recoverable via `navigator.credentials.get()` with no
 * `allowCredentials` (any local passkey for this RP). */
const LS_CRED_ID_KEY = "menese-2of2-credential-id-b64";

export interface PasskeyHandle {
  /** Raw bytes of the WebAuthn `credentialId`. Public; persisted
   * in localStorage for fast cross-session lookup. */
  credentialId: ArrayBuffer;
}

export interface EncryptedBlob {
  aead: Uint8Array; // AES-GCM ciphertext + tag
  salt: Uint8Array; // AES-GCM nonce (12 bytes)
  version: number; // schema version; v1 = 1
}

const ENCRYPTED_BLOB_VERSION = 1;
const AEAD_NONCE_BYTES = 12;

function bytesToBase64(bytes: ArrayBuffer): string {
  const arr = new Uint8Array(bytes);
  let bin = "";
  for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
  return btoa(bin);
}

function base64ToBytes(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr.buffer;
}

/** Register a fresh WebAuthn passkey with PRF extension. Called once
 * per principal at first onboarding. Persists the public credential
 * ID to localStorage. The PRF output is NOT persisted — re-derived
 * on every unwrap call. */
export async function passkeyRegister(
  iiPrincipal: Principal,
): Promise<PasskeyHandle> {
  // Use the principal as the WebAuthn user.id so the same II
  // identity always corresponds to the same WebAuthn user across
  // devices. NOT a secret.
  const userId = iiPrincipal.toUint8Array() as BufferSource;

  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)) as BufferSource,
      rp: { id: RP_ID, name: RP_NAME },
      user: {
        id: userId,
        name: iiPrincipal.toText(),
        displayName: "Menese 2-of-2",
      },
      pubKeyCredParams: [
        { type: "public-key", alg: -7 }, // ES256
        { type: "public-key", alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        residentKey: "required",
        userVerification: "required",
      },
      timeout: 60_000,
      attestation: "none",
      extensions: {
        prf: {
          eval: { first: PRF_INPUT_SALT.buffer },
        },
      } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!cred) {
    throw new Error("passkeyRegister: navigator.credentials.create returned null");
  }

  const ext = cred.getClientExtensionResults() as {
    prf?: { results?: { first?: ArrayBuffer } };
  };
  if (!ext.prf?.results?.first) {
    throw new Error(
      "passkeyRegister: WebAuthn-PRF extension not supported by this authenticator",
    );
  }

  localStorage.setItem(LS_CRED_ID_KEY, bytesToBase64(cred.rawId));

  return { credentialId: cred.rawId };
}

/** Re-derive the PRF salt for an existing passkey. Triggers a
 * `navigator.credentials.get()` user-verification gate. */
export async function passkeyDerivePrfSalt(): Promise<{
  prfSalt: ArrayBuffer;
  credentialId: ArrayBuffer;
}> {
  const credIdB64 = localStorage.getItem(LS_CRED_ID_KEY);
  let allowCredentials: PublicKeyCredentialDescriptor[] | undefined;
  if (credIdB64) {
    allowCredentials = [
      {
        type: "public-key",
        id: base64ToBytes(credIdB64),
      },
    ];
  }

  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)) as BufferSource,
      rpId: RP_ID,
      userVerification: "required",
      timeout: 60_000,
      allowCredentials,
      extensions: {
        prf: {
          eval: { first: PRF_INPUT_SALT.buffer },
        },
      } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!assertion) {
    throw new Error("passkeyDerivePrfSalt: assertion returned null");
  }
  const ext = assertion.getClientExtensionResults() as {
    prf?: { results?: { first?: ArrayBuffer } };
  };
  if (!ext.prf?.results?.first) {
    throw new Error(
      "passkeyDerivePrfSalt: WebAuthn-PRF result missing",
    );
  }
  // Persist (or re-confirm) the credential id.
  localStorage.setItem(LS_CRED_ID_KEY, bytesToBase64(assertion.rawId));
  return {
    prfSalt: ext.prf.results.first,
    credentialId: assertion.rawId,
  };
}

/** Derive a 256-bit AES-GCM key from the PRF salt via HKDF. */
async function deriveAeadKey(prfSalt: ArrayBuffer): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey(
    "raw",
    prfSalt,
    "HKDF",
    /*extractable*/ false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Uint8Array(0),
      info: HKDF_INFO_AEAD,
    },
    ikm,
    { name: "AES-GCM", length: 256 },
    /*extractable*/ false,
    ["encrypt", "decrypt"],
  );
}

/** Wrap `x_user` (32 raw bytes) under a fresh AES-GCM nonce. */
export async function wrapXUser(
  prfSalt: ArrayBuffer,
  xUser32: Uint8Array,
): Promise<EncryptedBlob> {
  if (xUser32.byteLength !== 32) {
    throw new Error(`wrapXUser: x_user must be 32 bytes, got ${xUser32.byteLength}`);
  }
  const key = await deriveAeadKey(prfSalt);
  const nonce = crypto.getRandomValues(new Uint8Array(AEAD_NONCE_BYTES));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as BufferSource },
    key,
    xUser32 as BufferSource,
  );
  return {
    aead: new Uint8Array(ct),
    salt: nonce,
    version: ENCRYPTED_BLOB_VERSION,
  };
}

/** Unwrap an `EncryptedBlob` to recover the 32-byte `x_user`. Returns
 * `null` on AEAD failure (wrong passkey / tampered ciphertext) — does
 * NOT throw, to avoid timing leaks. */
export async function unwrapXUser(
  prfSalt: ArrayBuffer,
  blob: EncryptedBlob,
): Promise<Uint8Array | null> {
  if (blob.version !== ENCRYPTED_BLOB_VERSION) return null;
  if (blob.salt.byteLength !== AEAD_NONCE_BYTES) return null;
  let key: CryptoKey;
  try {
    key = await deriveAeadKey(prfSalt);
  } catch {
    return null;
  }
  let plain: ArrayBuffer;
  try {
    plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: blob.salt as BufferSource },
      key,
      blob.aead as BufferSource,
    );
  } catch {
    return null;
  }
  const out = new Uint8Array(plain);
  if (out.byteLength !== 32) return null;
  return out;
}

/** Has the user already registered a passkey on THIS device (i.e. is
 * the credential ID cached in localStorage)? */
export function hasLocalCredentialId(): boolean {
  return localStorage.getItem(LS_CRED_ID_KEY) !== null;
}

/** Drop the cached credential ID. Intended for "log out + forget
 * device" flows. Does NOT delete the passkey itself (that lives in
 * the OS keychain). */
export function forgetLocalCredentialId(): void {
  localStorage.removeItem(LS_CRED_ID_KEY);
}
